import * as React from "react";
import { Notify } from "@/components/ui/notify";
import { cn } from "@/lib/utils";
import {
  useLineEvents,
  useLineManualEntries,
  type MachineEventSummary,
  type LineManualEntry,
} from "@/features/lines/hooks";
import { EventRow } from "./EventRow";
import { HistoryOverlay } from "./HistoryOverlay";
import type { UnifiedItem } from "./types";
import { EventDetailDialog } from "../EventDetailDialog";
import { ManualEntryDetailDialog } from "../ManualEntryDetailDialog";

export interface EventsListProps {
  lineId: string;
  limit?: number;
  className?: string;
}

export function EventsList({ lineId, limit = 12, className }: EventsListProps): React.ReactElement {
  const eventsQuery = useLineEvents(lineId, 50);
  const entriesQuery = useLineManualEntries(lineId);
  const { data: events, isLoading: eventsLoading } = eventsQuery;
  const { data: manualEntries, isLoading: entriesLoading } = entriesQuery;
  const [showAll, setShowAll] = React.useState(false);
  const [selectedEvent, setSelectedEvent] = React.useState<MachineEventSummary | null>(null);
  const [selectedEntry, setSelectedEntry] = React.useState<LineManualEntry | null>(null);

  const onRowSelect = (item: UnifiedItem) => {
    if (item.kind === "event") setSelectedEvent(item.event);
    else setSelectedEntry(item.entry);
  };

  const items = React.useMemo<UnifiedItem[]>(() => {
    const merged: UnifiedItem[] = [];
    (events ?? []).forEach((ev) => {
      merged.push({ kind: "event", _at: ev.startAt, id: `ev-${ev.id}`, event: ev });
    });
    (manualEntries ?? []).forEach((entry) => {
      const ts = entry.referenceAt ?? entry.createdAt;
      merged.push({ kind: "manual-entry", _at: ts, id: `me-${entry.id}`, entry });
    });
    merged.sort((a, b) => b._at.localeCompare(a._at));
    return merged;
  }, [events, manualEntries]);

  const isLoading = eventsLoading || entriesLoading;

  if (isLoading) {
    return (
      <ul className={cn("space-y-1", className)} aria-busy="true">
        {Array.from({ length: 4 }).map((_, i) => (
          <li key={i} className="bg-bg-muted h-9 rounded motion-safe:animate-pulse-dot" />
        ))}
      </ul>
    );
  }

  if (eventsQuery.isError || entriesQuery.isError) {
    return (
      <Notify.QueryError
        variant="inline"
        error={eventsQuery.isError ? eventsQuery.error : entriesQuery.error}
        onRetry={() => {
          void eventsQuery.refetch();
          void entriesQuery.refetch();
        }}
      />
    );
  }

  if (items.length === 0) {
    return (
      <Notify.Empty
        variant="inline"
        title="Sem eventos recentes"
        description="Eventos das máquinas e entradas manuais aparecem aqui quando ocorrem."
      />
    );
  }

  const visible = items.slice(0, limit);
  const hasMore = items.length > limit;

  return (
    <div className="space-y-2">
      <ol className={cn("divide-y divide-border-subtle", className)}>
        {visible.map((it) => (
          <EventRow key={it.id} item={it} onSelect={onRowSelect} />
        ))}
      </ol>
      {hasMore ? (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="rounded text-[12px] text-brand-700 hover:underline focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]"
        >
          Ver tudo ({items.length})
        </button>
      ) : null}
      {showAll ? <HistoryOverlay items={items} onClose={() => setShowAll(false)} /> : null}

      <EventDetailDialog
        event={selectedEvent}
        open={selectedEvent !== null}
        onClose={() => setSelectedEvent(null)}
      />
      <ManualEntryDetailDialog
        entry={selectedEntry}
        open={selectedEntry !== null}
        onClose={() => setSelectedEntry(null)}
      />
    </div>
  );
}
